import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, OnInit, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';

import { ButtonModule, CheckboxComponentModule, IconModule, LetModule } from '@hglobal/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, tap } from 'rxjs';

import { TabsComponent, TooltipComponent } from '@pp/common';
import { Tab } from '@pp/core/entities';
import { PpDialogService } from '@pp/core/services';

import { ConfirmShowNotificationsComponent } from '../confirm-show-notifications/confirm-show-notifications.component';
import { ConfirmShowNotificationsDialogData, Notification, NotificationsCount, NotificationType } from '../notifications.entities';
import { NotificationsStore } from '../notifications.store';
import { NotificationsListComponent } from './notifications-list/notifications-list.component';

@Component({
  selector: 'pp-notifications-history',
  imports: [
    CommonModule,
    ButtonModule,
    IconModule,
    TabsComponent,
    NotificationsListComponent,
    TranslateModule,
    LetModule,
    FormsModule,
    TooltipComponent,
    CheckboxComponentModule,
    MatSlideToggle,
  ],
  templateUrl: './notifications-history.component.html',
  styleUrl: './notifications-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsHistoryComponent implements OnInit {
  readonly #store = inject(NotificationsStore);
  readonly #cd = inject(ChangeDetectorRef);
  readonly #ppDialogService = inject(PpDialogService);
  readonly #translate = inject(TranslateService);
  readonly #tabsNames = ['messages', 'threeDS'];
  readonly #defaultTabsValue = [
    { index: 0, key: 'notifications.dialog.tab.messages.title' },
    { index: 1, key: 'notifications.dialog.tab.threeDS.title' },
  ];
  readonly #onlyThreeDS = [{ index: 1, key: 'notifications.dialog.tab.threeDS.title' }];
  readonly notificationTypeEnum = NotificationType;

  readonly closeDropdown = output<void>();

  readonly hasUnread = this.#store.hasUnread;
  readonly readAllInProgress = this.#store.isReadAllPending;
  readonly messages = this.#store.messages;
  readonly messagesLoading = this.#store.isGetMessagesPending;
  readonly threeDs = this.#store.threeDs;
  readonly threeDsLoading = this.#store.isGetThreeDsPending;
  readonly enableNotifications = signal(true);
  readonly initTabs = signal(true);

  enableNotificationsEffect = effect(() => {
    const enable = !this.#store.isNotificationsDisabled();

    untracked(() => {
      this.enableNotifications.set(enable);
      this.#initNotificationsTabs(enable);
    });
  });

  changeUnreadCountEffect = effect(() => {
    const unreadCount = this.#store.unreadCount();

    this.#setTabsCounters(unreadCount);
    this.#cd.markForCheck();
  });

  tabs: Tab[];
  tab = 'messages';

  ngOnInit() {
    this.tabs = this.#defaultTabsValue;
    this.#initMessages();
    this.#initThreeDs();
  }

  close(): void {
    this.closeDropdown.emit();
  }

  setTab(tabIndex: number): void {
    this.tab = this.#tabsNames[tabIndex];
  }

  loadMoreMessages(): void {
    this.#store.loadMoreMessages();
  }

  loadMoreThreeDs(): void {
    this.#store.loadMoreThreeDs();
  }

  markAsReadThreeDs(notification: Notification): void {
    this.markAsRead({ ...notification, type: NotificationType.THREE_DS_NOTIFICATION });
  }

  markAsRead(notification: Notification): void {
    this.#store.readMessage(notification);
  }

  markAllAsRead(): void {
    this.#store.readAll();
  }

  toggleNotifications(event: MatSlideToggleChange) {
    const enableMode = event.checked;
    const title = enableMode
      ? 'notifications.dialog.showNotifications.enable.title'
      : 'notifications.dialog.showNotifications.disable.title';
    this.#ppDialogService
      .open<ConfirmShowNotificationsComponent, ConfirmShowNotificationsDialogData>({
        title: this.#translate.instant(title),
        components: [ConfirmShowNotificationsComponent],
        data: {
          enableMode,
          saveMethod: this.#store.setNotificationSettings.bind(this),
          successSignal: this.#store.isSetNotificationsSettingsSuccess.bind(this),
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => this.enableNotifications.set(enableMode)),
        filter(() => this.enableNotifications()),
      )
      .subscribe(() => {
        this.#initMessages();
      });

    this.enableNotifications.set(!enableMode);
    this.#refreshTabsView();
  }

  #initMessages(): void {
    this.#store.getFirstPageMessages();
  }

  #initThreeDs(): void {
    this.#store.getFirstPageThreeDs();
  }

  #initNotificationsTabs(enable: boolean): void {
    if (enable) {
      this.tab = 'messages';
      this.tabs = this.#defaultTabsValue;
    } else {
      this.tab = 'threeDS';
      this.tabs = this.#onlyThreeDS;
    }

    this.#refreshTabsView();
  }

  #setTabsCounters(counters: NotificationsCount): void {
    if (!counters) {
      return;
    }

    const countersArr = [counters.unreadMessagesCount, counters.unread3DsCount];
    const tabs = this.enableNotifications() ? this.#defaultTabsValue : this.#onlyThreeDS;

    this.tabs = tabs.map(tab => ({ ...tab, unread: !!countersArr[tab.index] }));
  }

  #refreshTabsView(): void {
    setTimeout(() => this.initTabs.set(false));
    setTimeout(() => this.initTabs.set(true));
  }
}
